import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';
import { useState } from 'react';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import BulkTempalte941 from '@/components/ui/BulkTempalte941';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const PayrollTax = () => {
  const [isBulkUploadTemplate, setIsBulkUploadTemplate] = useState(false);
  return (
    <AppLayout
      meta={
        <Meta
          title="Employer Payroll Taxes Explained for the 2026 tax year"
          description="Get a complete guide to employer payroll taxes for 2026. Learn what taxes employers must pay, current rates, how to calculate them, due dates, and penalties."
          keywords=""
          canonical="resources/employment-tax/employer-payroll-taxes/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <BulkTempalte941
        isBulkUploadTemplate={isBulkUploadTemplate}
        setIsBulkUploadTemplate={setIsBulkUploadTemplate}
      />
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          Employer{' '}
          <span className="highlight-container">
            <span className="highlight"> Payroll Taxes:</span>
          </span>{' '}
          Everything You Need to Know
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
            textAlign: 'center',
          }}
        >
          Learn about employer payroll tax responsibilities and file <br />
          your Form 940, Form 941 with TaxZerone for a quick process.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=payroll`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/employer-payroll-taxes/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Employer Payroll Taxes at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <b>Employer payroll taxes</b>
                        {
                          ' are taxes employers pay based on their employees’ wages. These taxes are separate from amounts withheld from employees’ paychecks.'
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Social Security Tax:</b>
                        {' Employers generally pay '}
                        <b>6.2%</b>
                        {' on an employee’s wages, up to the '}
                        <b>$184,500 wage base for 2026</b>.
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Medicare Tax:</b>
                        {' Employers generally pay '}
                        <b>1.45%</b>
                        {' on all covered wages, with '}
                        <b>no wage limit</b>.
                      </span>
                    </li>
                    <li>
                      <span>
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/futa-tax/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>FUTA Tax</b>
                        </Link>
                        <b>:</b>
                        {' Employers generally pay '}
                        <b>6.0%</b>
                        {' on the first '}
                        <b>$7,000</b>
                        {' of wages per employee. A credit of up to '}
                        <b>5.4%</b>
                        {' may reduce the effective rate to '}
                        <b>0.6%</b>.
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>SUTA Tax:</b>
                        {' State unemployment tax rates and wage bases '}
                        <b>vary by state</b>
                        {' and may depend on the employer’s experience rating.'}
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Reporting:</b>
                        {' Employers'} {'report federal payroll taxes on '}
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-941-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Form 941</b>
                        </Link>
                        {' and FUTA taxes on '}
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-940-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Form 940</b>
                        </Link>
                        .
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Understanding employer payroll taxes is important to stay
                  compliant with federal and state regulations and running a
                  smooth payroll system. This resource guides you to everything
                  employers need to know about payroll taxes—from what they are
                  to how they are calculated.
                </Typography>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What are Employer Payroll Taxes?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#which" sx={{ color: '#0202ff' }}>
                          Which payroll taxes are paid by employers?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatare" sx={{ color: '#0202ff' }}>
                          What are the current employer payroll tax rates?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howare" sx={{ color: '#0202ff' }}>
                          How are employer payroll taxes calculated?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When are employer payroll taxes due?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#income" sx={{ color: '#0202ff' }}>
                          Income Tax vs. Payroll Tax
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#pen" sx={{ color: '#0202ff' }}>
                          Penalties
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What are Employer Payroll Taxes?
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Employer payroll tax is paid by an employer based on their
                    employee wages.
                  </li>
                  <li>
                    These taxes are separate from the amounts withheld from
                    employee wages.
                  </li>
                  <li>
                    These taxes are used to fund programs such as Social
                    Security, Medicare, and unemployment benefits.
                  </li>
                  <li>
                    Employers should withhold the correct tax amounts, pay their
                    share, and file payroll tax forms like{' '}
                    <Link
                      href="/resources/employment-tax/form-941-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 941
                    </Link>{' '}
                    and{' '}
                    <Link
                      href="/resources/employment-tax/form-940-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 940
                    </Link>{' '}
                    regularly.
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { md: 17, sm: 17, xs: 16 },
                          fontWeight: 600,
                        }}
                      >
                        Need help filing Form 941 or 940?
                      </Typography>
                      <Typography variant="body1">
                        TaxZerone makes e-filing simple, secure, and
                        IRS-authorized.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=payroll`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="which"
                >
                  Which payroll taxes are paid by employers?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  These are the payroll taxes paid by the employers:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Employer portion of Social Security & Medicare tax ({' '}
                    <Link
                      href="/resources/employment-tax/fica-tax/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      FICA
                    </Link>
                    )
                  </li>
                  <li>
                    Federal Unemployment Tax Act ({' '}
                    <Link
                      href="/resources/employment-tax/futa-tax/"
                      sx={{ color: '#0202ff' }}
                    >
                      FUTA
                    </Link>
                    )
                  </li>
                  <li>State Unemployment Tax Act (SUTA)</li>
                  <li>State or Local Payroll Taxes (if applicable) </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b>&nbsp; Employers also withhold the employee&apos;s
                  portion of Social Security, Medicare, and federal/state income
                  taxes from wages.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatare"
                >
                  What are the current employer payroll tax rates?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Employer payroll taxes include the following percentages as of
                  2026:
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Tax Type
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Rate
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Social Security </TableCell>
                          <TableCell>
                            6.2% (on first $184,500 of wages){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Medicare </TableCell>
                          <TableCell>1.45% (no wage limit) </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Federal Unemployment (FUTA) </TableCell>
                          <TableCell>
                            6.0% (on earning of first $7,000; up to 5.4% credit
                            available){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>State Unemployment (SUTA) </TableCell>
                          <TableCell>
                            Varies by state and experience rating
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="howare"
                >
                  How are employer payroll taxes calculated?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Employer payroll taxes are calculated based on the employee’s
                  wage limit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Employer Payroll Tax</span>{' '}
                  = (Gross Wages × Social Security Rate) + (Gross Wages ×
                  Medicare Rate) + (Wages up to $7,000 × FUTA Rate) + (Wages up
                  to state limit × SUTA Rate)
                </Typography>
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: '#dfe7fd',
                    p: 2,
                    borderRadius: 3,

                    border: '2px solid #3e66d4ff',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      mt: 1,
                      fontStyle: 'italic',
                      mb: 0.8,
                    }}
                  >
                    Example 1:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Let us take an employee earning $50,000 annually. Calculate
                    the Payroll tax.
                  </Typography>
                  <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                    <li> Social Security (6.2%) = $50,000 x 6.2% = $3,100</li>
                    <li> Medicare (1.45%) = $50,000 x 1.45% = $725</li>
                    <li>
                      {' '}
                      FUTA (after credit, 0.6% on $7,000) = $7,000 x 0.6% = $42
                    </li>
                    <li>
                      {' '}
                      SUTA (assume 2.7% on $7,000) = $7,000 x 2.7% = $189
                    </li>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 10, mb: 1 }}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Total Employer Payroll Tax
                    </span>{' '}
                    = $4,056{' '}
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="when"
                >
                  When are employer payroll taxes due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Employer payroll taxes are generally due based on the
                  employer’s payroll schedule and tax liability. Here’s a
                  breakdown:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  1. Federal Payroll Tax Deposits
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  There are two ways to deposit your payroll taxes based on your
                  total tax liability during the IRS lookback period.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Monthly Depositor{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Reported a total tax of{' '}
                    <span style={{ fontWeight: 600 }}> $50,000 or less</span>{' '}
                    during the lookback period.{' '}
                  </li>
                  <li>
                    Deposit your payroll taxes by the{' '}
                    <span style={{ fontWeight: 600 }}>15th </span>of the
                    following month.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Semiweekly Depositor
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Reported a total tax of{' '}
                    <span style={{ fontWeight: 600 }}> more than $50,000 </span>
                    during the lookback period.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Deposit deadlines: </span>
                  </li>
                </Box>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            If the payday falls on
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Then deposit your taxes by the following
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Wednesday, Thursday, and/or Friday{' '}
                          </TableCell>
                          <TableCell>Wednesday </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Saturday, Sunday, Monday, and/or Tuesday{' '}
                          </TableCell>
                          <TableCell>Friday </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  2. IRS Employment Tax Forms
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Form
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Purpose
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Form 941
                          </TableCell>
                          <TableCell>Quarterly federal tax return </TableCell>
                          <TableCell>
                            Last day of the month following the quarter{' '}
                            <br style={{ marginBottom: 8 }} />
                            Quarter 1 -{' '}
                            <span style={{ fontWeight: 600 }}>April 30</span>
                            <br style={{ marginBottom: 8 }} />
                            Quarter 2 -{' '}
                            <span style={{ fontWeight: 600 }}>
                              July 31
                            </span>{' '}
                            <br style={{ marginBottom: 8 }} />
                            Quarter 3 -{' '}
                            <span style={{ fontWeight: 600 }}>
                              October 31
                            </span>{' '}
                            <br style={{ marginBottom: 8 }} />
                            Quarter 4 -{' '}
                            <span style={{ fontWeight: 600 }}>January 31</span>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Form 940
                          </TableCell>
                          <TableCell>Annual FUTA tax return </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              January 31 (or February 10
                            </span>{' '}
                            if FUTA taxes were deposited on time){' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  3. State Payroll Taxes
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Due dates vary by state. </li>
                  <li>
                    Common schedules include monthly, quarterly, or
                    annually.{' '}
                  </li>
                  <li>
                    Check with your state’s Employment Department for specific
                    deadlines.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="income"
                >
                  Income Tax vs Payroll Tax
                </Typography>

                <Typography variant="body1" sx={{ mb: 0.8 }}>
                  Let us discuss the differences between income tax and Payroll
                  tax.
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Feature
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Income Tax
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Payroll Tax
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Who Pays?
                          </TableCell>
                          <TableCell>
                            Employees (withheld by employer){' '}
                          </TableCell>
                          <TableCell>Both employers and employees</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Purpose
                          </TableCell>
                          <TableCell>General government revenue </TableCell>
                          <TableCell>
                            Funds Social Security, Medicare, Unemployment
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Based On
                          </TableCell>
                          <TableCell>Total income</TableCell>
                          <TableCell>Wages paid </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Who Collects?
                          </TableCell>
                          <TableCell>
                            IRS (Federal) & State tax agencies{' '}
                          </TableCell>
                          <TableCell>
                            IRS (FICA, FUTA), State agencies
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="pen"
                >
                  Penalties
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Failing to file or{' '}
                  <Link
                    href="/resources/employment-tax/form-941-944-deposit-requirements/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    deposit payroll taxes
                  </Link>{' '}
                  can result in{' '}
                  <Link
                    href="/resources/employment-tax/form-941-penalties/"
                    sx={{ color: '#0202ff' }}
                  >
                    941 penalties
                  </Link>
                  :
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Scenario
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Penalty
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Late Deposit{' '}
                          </TableCell>

                          <TableCell>
                            2% to 15% of the unpaid tax, depending on how late
                            the payment is
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Late Filing{' '}
                          </TableCell>

                          <TableCell>
                            5% of the unpaid tax per month, up to a maximum of
                            25%
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Late Payment{' '}
                          </TableCell>

                          <TableCell>
                            0.5% of the unpaid tax per month, up to 25%
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Intentional Disregard or Fraud{' '}
                          </TableCell>

                          <TableCell>
                            May result in criminal charges, higher fines, and
                            imprisonment
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Trust Fund Recovery Penalty (TFRP){' '}
                          </TableCell>

                          <TableCell>
                            100% of the unpaid employee tax withheld (Social
                            Security & Medicare)
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Source and Reference
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/p15.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          2025 Publication 15
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.irs.gov/taxtopics/tc751"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          TC751
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.irs.gov/payments/failure-to-file-penalty"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        Late filing penalty,{' '}
                        <Link
                          href="https://www.irs.gov/payments/failure-to-pay-penalty"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        Late Payment Penalty,{' '}
                        <Link
                          href="https://www.irs.gov/payments/failure-to-deposit-penalty"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        late deposit Penalty
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Simplify Your Payroll Tax Filing with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File your employer payroll taxes easily and securely with
              TaxZerone.
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>
              Stay compliant and avoid penalties!
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=payroll`}
              >
                E-File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default PayrollTax;
